import React, { useState, useMemo } from 'react';
import styled from '@emotion/styled';

import api from 'api';
import useMountEffect from 'hooks/useMountEffect';
import useReports from 'hooks/useReports';
import useRequest from 'hooks/useRequest';
import { BurncenterAccessLevelEnum } from 'utils/enums';
import Labels from 'utils/labels';

import Loader from 'components/elements/Loader';
import colors from 'resources/styles/colors';
import {
  FlexCenter,
  FlexColumnCenterAlign,
} from 'resources/styles/display';

import ReportViewportArea from './ReportViewportArea';
import { ReportDetailViewportWrapper } from './reportsViewStyles';

const FrameWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-height: 0;

  iframe {
    flex: 1;
    width: 100%;
    min-height: 0;
    border: none;
  }
`;

interface WorkspacePageMsgProps {
  children: React.ReactNode;
}

const WorkspacePageMsg: React.FC<WorkspacePageMsgProps> = ({ children }) => (
  <FlexCenter
    style={{ flex: 1, color: colors.darkGray, textAlign: 'center' }}
  >
    {children}
  </FlexCenter>
);

interface BurncenterData {
  sigmaWorkspaceLandingLink?: string;
  jwtSigmaWorkspaceLandingLink?: string;
  burnCenterAccessLevel?: typeof BurncenterAccessLevelEnum[keyof typeof BurncenterAccessLevelEnum];
}

interface WorkspaceData {
  url: string;
}

const WorkspacePage: React.FC = () => {
  const { currentUser, impersonateUserId } = useReports();

  const [burncenter, setBurncenter] = useState<BurncenterData | null>(null);

  const {
    doRequest: getDashboard,
    data: workspaceData,
    isLoading: loadingWorkspace,
  } = useRequest(api.getDashboard);

  useMountEffect(() => {
    if (!currentUser?.burnCenterId) return;

    api.getBurnCenter(currentUser.burnCenterId).then(({ data }) => {
      setBurncenter(data);
      if (!data?.sigmaWorkspaceLandingLink) return;
      if (!data?.jwtSigmaWorkspaceLandingLink) return;

      getDashboard('my-workspace', { impersonateUserId });
    });
  });

  const renderContent = useMemo(() => {
    if (!burncenter || loadingWorkspace) return <Loader />;

    if (
      burncenter.burnCenterAccessLevel !== BurncenterAccessLevelEnum.FULL_PLATFORM
    ) {
      return (
        <WorkspacePageMsg>
          <FlexColumnCenterAlign style={{ gap: '1rem', fontSize: '1.5rem' }}>
            <span>{`${Labels.workspace} is available to BCQP Full Platform burn centers.`}</span>
            <span>
              Please reach out to
              {' '}
              <a
                href="mailto:support-bcqp@bdatainc.com"
                rel="noopener noreferrer"
                target="_blank"
                style={{ color: colors.secondaryColor }}
              >
                support-bcqp@bdatainc.com
              </a>
              {' '}
              to learn more.
            </span>
          </FlexColumnCenterAlign>
        </WorkspacePageMsg>
      );
    }

    if (!burncenter.sigmaWorkspaceLandingLink || !burncenter.jwtSigmaWorkspaceLandingLink) {
      return (
        <WorkspacePageMsg>
          <h2>⚙ Coming Soon</h2>
        </WorkspacePageMsg>
      );
    }

    if (!workspaceData) return <Loader />;

    return (
      <FrameWrapper>
        <iframe
          title="workspaceData"
          src={(workspaceData as WorkspaceData).url}
        />
      </FrameWrapper>
    );
  }, [burncenter, loadingWorkspace, workspaceData]);

  return (
    <ReportDetailViewportWrapper>
      <ReportViewportArea>
        {renderContent}
      </ReportViewportArea>
    </ReportDetailViewportWrapper>
  );
};

export default WorkspacePage;
